// import { CognitoUser } from "@aws-amplify/auth";
import { Hub, HubCallback } from "@aws-amplify/core";
import { signOut as cognitoSignOut, fetchAuthSession } from "aws-amplify/auth";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
const baseUrl = process.env.REACT_APP_REMIX_DOMAIN;
export interface IUser {
  id: string;
  email: string;
  region: string;
  branch: string;
  fullName: string;
  role: string[];
}

export enum LoginState {
  LoggedOut = "LoggedOut",
  Unknown = "Unknown",
}
export type UserState = LoginState | IUser;

export interface IAuthContext {
  user: UserState;
  signOut: () => void;
  isSignedIn: () => boolean;
  clearAuthState: () => void;
}

interface IProps {
  children?: React.ReactNode;
}

export const AuthContext = createContext<IAuthContext>({
  user: LoginState.Unknown,
  signOut: () => undefined,
  isSignedIn: () => false,
  clearAuthState: () => undefined,
});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }: IProps) => {
  const auth = useCognito();

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

const signOut = async () => {
  try {
    await cognitoSignOut();
    document.cookie = `idToken=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT;domain=${baseUrl};SameSite=Strict;Secure`;
    document.cookie = `accessToken=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT;domain=${baseUrl};SameSite=Strict;Secure`;
  } catch (error) {
    console.error(error);
  }
};

const ensureValidUser = async (user: any) => {
  const currentUser = await fetchAuthSession();
  if (user.userId === currentUser.userSub) return currentUser;
  else throw new Error("Invalid user");
};

const loggedOutStates: unknown[] = [LoginState.LoggedOut, LoginState.Unknown];
const isLoggedIn = (value: LoginState | IUser): value is IUser => !loggedOutStates.includes(value);

const getUserObject = (attributes: any) => {
  const tempAttribute = attributes.tokens.idToken.payload;

  return {
    id: tempAttribute.sub,
    email: tempAttribute.email,
    region: tempAttribute["custom:region"],
    branch: tempAttribute["custom:home_branch"],
    fullName: [tempAttribute.given_name, tempAttribute.family_name].filter(Boolean).join(" "),
    role: tempAttribute["cognito:groups"],
  };
};
export const useCognito = () => {
  const [user, setUser] = useState<UserState>(LoginState.Unknown);
  const clearAuthState = () => setUser(LoginState.Unknown);

  const isSignedIn = useCallback(() => isLoggedIn(user), [user]);

  const setOrFetchUser = async (data: unknown) => {
    try {
      const attributes = await ensureValidUser(data);
      const exp = attributes?.tokens?.idToken?.payload?.exp ?? 0;
      const expiryDate = new Date(exp * 1000).toString();

      document.cookie = `idToken=${attributes.tokens?.idToken};path=/;expires=${expiryDate};domain=${baseUrl};SameSite=Strict;Secure`;
      document.cookie = `accessToken=${attributes?.tokens?.accessToken};path=/;expires=${expiryDate};domain=${baseUrl};SameSite=Strict;Secure`;
      setUser(getUserObject(attributes));
    } catch (err) {
      console.error(err);
    }
  };

  const authListener: HubCallback = useCallback(({ payload: { event, data } }) => {
    switch (event) {
      case "signedIn":
        setOrFetchUser(data);
        break;
      case "signedOut":
        setUser(LoginState.LoggedOut);
        break;
    }
  }, []);

  useEffect(() => {
    fetchAuthSession()
      .then(attributes => {
        setUser(getUserObject(attributes));
      })
      .catch(() => {
        setUser(LoginState.LoggedOut);
      });
  }, []);

  useEffect(() => {
    const hubListener = Hub.listen("auth", authListener);
    return () => hubListener();
  }, [authListener]);

  return { user, signOut, clearAuthState, isSignedIn };
};
